import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CompensationsPayrollConceptsDocument,
  PayrollCreateConceptDocument,
  PayrollValueObjectsConceptCategoryEnum,
  PayrollValueObjectsConceptUnitTypeEnum,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  DEFAULT_CONCEPT_UNIT_TYPE,
  payrollConceptCategoriesOptions,
  payrollConceptUnitTypeOptions,
} from 'modules/payroll_bundle/lib/constants'

interface ConceptForm {
  companyId: number
  category: PayrollValueObjectsConceptCategoryEnum
  name: string
  description?: string
  unitType: PayrollValueObjectsConceptUnitTypeEnum
}

interface Props {
  category: PayrollValueObjectsConceptCategoryEnum
  onSuccess: () => void
  onClose: () => void
  legacyPayroll?: boolean
}

const AddConceptModal = ({ category, onSuccess, onClose, legacyPayroll = false }: Props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [create] = useMutation(PayrollCreateConceptDocument, {
    extractorFn: (result) => result.payrollMutations.createPayrollConcept,
  })

  const form = useForm<ConceptForm>({
    defaultValues: {
      companyId,
      category,
      name: '',
      unitType: DEFAULT_CONCEPT_UNIT_TYPE,
    },
    validate(values) {
      const errors: FormErrors<ConceptForm> = {}

      if (values.name === '') {
        errors.name = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await create({
        variables: {
          companyId: values.companyId,
          label: values.name,
          description: values.description,
          category: values.category,
          unitType: values.unitType,
        },
        refetchQueries: [CompensationsPayrollConceptsDocument],
      })

      if (!legacyPayroll) {
        track(trackingEvents.compensations.createPayrollConcept, {
          conceptName: values.name,
          category: values.category,
        })
      }

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success && handled.data.concept) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onSuccess()
      }

      return handled
    },
  })

  return (
    <Modal
      illustration="edit-file"
      title={i18n.t('compensations.settings.concepts.add_concept')}
      description={i18n.t('compensations.settings.concepts.add_concept_description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting || !form.isValid}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <SelectField
          label={i18n.t('compensations.settings.concepts.form.category')}
          value={form.values.category}
          onChange={(value: string) => form.setFieldValue('category', value)}
          options={payrollConceptCategoriesOptions()}
        />
        <TextField
          label={i18n.t('compensations.settings.concepts.form.name')}
          value={form.values.name}
          onChange={(value: string) => form.setFieldValue('name', value)}
          mandatory
          error={form.touched.name ? form.errors.name : undefined}
        />
        {!legacyPayroll && (
          <SelectField
            label={i18n.t('compensations.settings.concepts.form.unit_type')}
            value={form.values.unitType}
            onChange={(value: string) => form.setFieldValue('unitType', value)}
            mandatory
            options={payrollConceptUnitTypeOptions()}
          />
        )}
        <TextArea
          label={i18n.t('compensations.settings.concepts.form.description')}
          maxLength={250}
          minRows={3}
          width={400}
          value={form.values.description}
          onChange={(value: string) => form.setFieldValue('description', value)}
        />
      </Box>
    </Modal>
  )
}

export default AddConceptModal
